<div class="relative flex h-full min-h-0 flex-col">
  <!-- Opaque: cards scroll directly under it, and a translucent header ghosts their borders. -->
  <div
    #stickyHeader
    class="sticky top-0 z-20 border-b border-neutral-200 bg-neutral-0 pb-2"
  >
    <guarantees-toolbar
      [filters]="filters()"
      [facets]="facets()"
      [icd10Facets]="icd10Facets()"
      [rangePreset]="rangePreset()"
      [viewMode]="viewMode()"
      [queueCount]="decisionQueueCount()"
      [deliveryGapCount]="deliveryGapCount()"
      [prescriptionCount]="prescriptionRowCount()"
      [visitCount]="visitCount()"
      [breachCount]="breachCount()"
      [caseCount]="caseCount()"
      [showDeleted]="showDeleted()"
      [sidePanel]="sidePanel()"
      [busy]="loading() || resolving()"
      [readOnly]="readOnly()"
      (searchChanged)="search($event)"
      (rangePresetChanged)="selectPreset($event)"
      (viewModeChanged)="selectViewMode($event)"
      (decisionToggled)="toggleDecision($event)"
      (kindToggled)="toggleKind($event)"
      (fulfillmentToggled)="toggleFulfillment($event)"
      (statusToggled)="toggleStatus($event)"
      (icd10PrefixToggled)="toggleIcd10($event)"
      (icd10CursorChanged)="moveIcd10Cursor($event)"
      (icd10Cleared)="clearIcd10()"
      (filtersCleared)="clearFilters()"
      (showDeletedToggled)="toggleDeleted()"
      (panelToggled)="togglePanel($event)"
    ></guarantees-toolbar>

    <!-- Only present once a link has been followed or a jump is in flight. -->
    @if (canNavigateBack() || resolving() || resolveError() || saveError()) {
      <div class="mt-1.5 flex flex-wrap items-center gap-2 px-1">
        @if (canNavigateBack()) {
          <button
            class="inline-flex shrink-0 items-center gap-1 rounded-lg border border-neutral-200 bg-neutral-0 px-2 py-1 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
            type="button"
            [matTooltip]="'guaranteeV2.message.navigateBackHint' | translate"
            (click)="navigateBack()"
          >
            <mat-icon class="h-4! w-4! text-base! leading-4!">arrow_back</mat-icon>
            @if (backTarget(); as target) {
              {{ "guaranteeV2.action.backToVisit" | translate: { date: target.date | date: "d MMM yyyy" } }}
            } @else {
              {{ "guaranteeV2.action.back" | translate }}
            }
          </button>
        }

        @if (resolving()) {
          <span
            class="inline-flex items-center gap-1 text-sm text-neutral-500"
            role="status"
          >
            <guarantees-spinner class="text-primary-500"></guarantees-spinner>
            {{ "guaranteeV2.message.resolvingTarget" | translate }}
          </span>
        }

        @if (resolveError(); as message) {
          <span
            class="inline-flex items-center gap-1 rounded-lg bg-warning-50 px-2 py-1 text-sm font-medium text-warning-800"
            role="alert"
            [matTooltip]="'guaranteeV2.message.resolveErrorHint' | translate"
          >
            <mat-icon class="h-4! w-4! text-base! leading-4!">link_off</mat-icon>
            {{ message | translate }}
          </span>
        }

        <!--
          A refused write leaves the row showing its old value with nothing to explain why, so the
          failure is stated rather than left to be inferred from a change that did not stick.
        -->
        @if (saveError(); as message) {
          <span
            class="inline-flex items-center gap-1 rounded-lg bg-error-50 px-2 py-1 text-sm font-medium text-error-700"
            role="alert"
            [matTooltip]="'guaranteeV2.message.writeErrorHint' | translate"
          >
            <mat-icon class="h-4! w-4! text-base! leading-4!">sync_problem</mat-icon>
            {{ message | translate }}
          </span>
        }
      </div>
    }
  </div>

  <!-- Panels take their own column, which is what keeps them reachable from every view mode. -->
  <div class="flex min-h-0 flex-1">
    <!-- A gutter, so the stream's cards never sit flush against the panel's edge. -->
    <div
      class="min-h-0 min-w-0 flex-1 overflow-y-auto pt-2 pb-4"
      [class]="sidePanel() ? 'max-lg:hidden lg:pr-3' : ''"
    >
      @if (loading()) {
        <div class="flex flex-col gap-1.5">
          @for (row of skeletonRows; track row) {
            <div
              class="animate-pulse rounded-xl border border-neutral-200 bg-neutral-0 px-2.5 py-3"
              [style.animation-delay.ms]="row * 90"
            >
              <div class="flex items-center gap-2">
                <span class="size-7 shrink-0 rounded-lg bg-neutral-100"></span>
                <span class="h-3 w-24 rounded bg-neutral-100"></span>
                <span class="h-3 w-16 rounded bg-neutral-100"></span>
                <span class="ml-auto h-4 w-20 rounded-full bg-neutral-100"></span>
              </div>
              <div class="mt-2 flex gap-2 pl-9">
                <span class="h-2.5 w-32 rounded bg-neutral-100"></span>
                <span class="h-2.5 w-20 rounded bg-neutral-100"></span>
              </div>
            </div>
          }
        </div>
      } @else if (error(); as message) {
        <div class="flex flex-col items-center gap-3 rounded-2xl border border-error-200 bg-error-50 px-4 py-10 text-center">
          <mat-icon class="text-3xl! text-error-500">error_outline</mat-icon>
          <p class="text-base text-error-700">{{ message | translate }}</p>
          <button
            class="btn btn-primary focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
            type="button"
            (click)="reload()"
          >
            {{ "guaranteeV2.action.retry" | translate }}
          </button>
        </div>
      } @else if (isEmpty()) {
        <div class="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-neutral-200 py-14 text-center text-neutral-400">
          <mat-icon class="text-4xl!">event_busy</mat-icon>

          <!-- Only once the whole history is read is "no guarantees" a true statement. -->
          @if (widerRange(); as preset) {
            <p class="text-base">{{ "guaranteeV2.message.emptyRange" | translate }}</p>
            <button
              class="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
              type="button"
              (click)="selectPreset(preset)"
            >
              <mat-icon class="h-4! w-4! text-base! leading-4!">date_range</mat-icon>
              {{ "guaranteeV2.action.extendRange" | translate: { range: rangeLabelKey(preset) | translate } }}
            </button>
          } @else {
            <p class="text-base">{{ "guaranteeV2.message.emptyTimeline" | translate }}</p>
          }
        </div>
      } @else if (noResults()) {
        <!-- The window has history; the filters are what emptied the view, so say so and offer the way out. -->
        <div class="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-neutral-200 py-14 text-center text-neutral-400">
          <mat-icon class="text-4xl!">filter_alt_off</mat-icon>
          <p class="text-base">{{ "guaranteeV2.message.noSearchResults" | translate }}</p>
          <span class="flex flex-wrap items-center justify-center gap-2">
            <button
              class="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
              type="button"
              (click)="clearFilters()"
            >
              {{ "guaranteeV2.action.clearFilters" | translate }}
            </button>

            <!-- What was searched for may simply be older than the window, so widening is offered too. -->
            @if (widerRange(); as preset) {
              <button
                class="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
                type="button"
                (click)="selectPreset(preset)"
              >
                <mat-icon class="h-4! w-4! text-base! leading-4!">date_range</mat-icon>
                {{ "guaranteeV2.action.extendRange" | translate: { range: rangeLabelKey(preset) | translate } }}
              </button>
            }
          </span>
        </div>
      } @else if (viewMode() === "prescriptions") {
        <guarantees-prescription-table
          [rows]="prescriptionRows()"
          (guaranteeOpened)="openGuarantee($event)"
        ></guarantees-prescription-table>
      } @else if (viewMode() === "decisions") {
        <guarantees-decision-queue
          [queue]="decisionQueue()"
          [readOnly]="readOnly()"
          (guaranteeOpened)="openGuarantee($event)"
        ></guarantees-decision-queue>
      } @else if (viewMode() === "delivery") {
        <guarantees-delivery-gaps
          [gaps]="deliveryGaps()"
          (guaranteeOpened)="openGuarantee($event)"
        ></guarantees-delivery-gaps>
      } @else {
        <guarantees-care-stream
          [visits]="visits()"
          [readOnly]="readOnly()"
          [focusedGuaranteeId]="focusedGuaranteeId()"
          (caseOpened)="openCase($event)"
          (detailsOpened)="openGuaranteeDetails($event)"
          (prescriptionDetailsOpened)="openPrescriptionDetails($event)"
        ></guarantees-care-stream>
      }
    </div>

    <!--
      Sticky: nothing above this tab constrains its height — the shell's main is `min-h`, so the
      document scrolls and a panel sitting in the row would be as tall as the stream.

      It rests against the MEASURED toolbar height, never a guessed offset: the toolbar is sticky too
      and grows a second row once something is filtered.
    -->
    @if (sidePanel(); as panel) {
      <aside
        class="sticky flex w-full shrink-0 flex-col self-start border-l border-neutral-200 bg-neutral-0 transition duration-200 ease-out motion-reduce:transition-none lg:w-104 xl:w-120 starting:translate-x-2 starting:opacity-0"
        [style.top.px]="headerHeight()"
        [style.height]="'calc(100dvh - ' + headerHeight() + 'px)'"
        [attr.aria-label]="panelTitleKeys[panel] | translate"
      >
        <header class="flex items-center gap-2 border-b border-neutral-200 px-2.5 py-2">
          <!-- Findings carry their own colour: the panel is a warning, not another record. -->
          <span
            class="flex size-7 shrink-0 items-center justify-center rounded-lg"
            [class]="panel === 'breaches' ? 'bg-warning-100 text-warning-700' : 'bg-primary-50 text-primary-700'"
          >
            <mat-icon class="h-4! w-4! text-base! leading-4!">{{ panelIcons[panel] }}</mat-icon>
          </span>

          <span
            class="min-w-0 flex-1 truncate text-base font-semibold text-neutral-700"
            [matTooltip]="panelHintKeys[panel] | translate"
            >{{ panelTitleKeys[panel] | translate }}</span
          >

          <button
            class="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
            type="button"
            [matTooltip]="'guaranteeV2.action.close' | translate"
            (click)="closePanel()"
          >
            <mat-icon class="h-4! w-4! text-base! leading-4!">close</mat-icon>
          </button>
        </header>

        <div class="min-h-0 flex-1 overflow-y-auto px-2.5 py-2">
          @switch (panel) {
            @case ("cases") {
              <!-- Renders with no cases: it is the only route to opening the first one. -->
              <guarantees-case-panel
                [cases]="caseViews()"
                [focusCaseId]="focusedCaseId()"
                [readOnly]="readOnly()"
                (guaranteeSelected)="openGuarantee($event)"
              ></guarantees-case-panel>
            }
            @case ("breaches") {
              <!--
                Findings describe the loaded window, so nothing is claimed about one that has not
                arrived: "none found" over a window still loading is a statement about the last one.
              -->
              @if (error(); as message) {
                <p class="px-1 py-2 text-sm text-error-600">{{ message | translate }}</p>
              } @else if (loading()) {
                <guarantees-spinner
                  class="text-neutral-400"
                  role="status"
                ></guarantees-spinner>
              } @else {
                <guarantees-breach-list [rows]="breachRows()"></guarantees-breach-list>
              }
            }
            @case ("guarantee") {
              <!-- Follows the window rather than holding a copy, so writes show without reopening. -->
              @if (detailsVisit(); as visit) {
                <guarantees-guarantee-details
                  [visit]="visit"
                  (guaranteeOpened)="openGuarantee($event)"
                ></guarantees-guarantee-details>
              } @else {
                <p class="px-1 py-2 text-sm text-neutral-400">{{ "guaranteeV2.message.guaranteeGone" | translate }}</p>
              }
            }
            @case ("prescription") {
              @if (detailsLine(); as line) {
                <guarantees-prescription-details
                  [line]="line"
                  (guaranteeOpened)="openGuarantee($event)"
                ></guarantees-prescription-details>
              } @else {
                <p class="px-1 py-2 text-sm text-neutral-400">{{ "guaranteeV2.message.prescriptionGone" | translate }}</p>
              }
            }
          }
        </div>
      </aside>
    }
  </div>
</div>
